/**
 * Analytics tracking utility for admin dashboard
 * Tracks page views, feature usage, and session data
 */

const API = process.env.REACT_APP_BACKEND_URL;

// Detect device type
const getDeviceType = () => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Session tracking
let sessionStartTime = Date.now();
let currentPage = null;
let pageStartTime = null;

// Track analytics event
export const trackEvent = async (eventType, data = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return; // Don't track unauthenticated users
    
    await fetch(`${API}/api/admin/track`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        event_type: eventType,
        device_type: getDeviceType(),
        user_agent: navigator.userAgent,
        screen_width: window.innerWidth,
        timestamp: new Date().toISOString(),
        ...data
      })
    });
  } catch (error) {
    // Silently fail - don't disrupt user experience for analytics
    console.debug('Analytics tracking failed:', error);
  }
};

// Track page view
export const trackPageView = (pageName) => {
  // Send duration for previous page
  if (currentPage && pageStartTime) {
    const duration = Math.round((Date.now() - pageStartTime) / 1000);
    trackEvent('page_view', {
      page: currentPage,
      duration: duration
    });
  }
  
  // Start tracking new page
  currentPage = pageName;
  pageStartTime = Date.now();
  
  trackEvent('page_view', {
    page: pageName,
    duration: 0
  });
};

// Track feature usage
export const trackFeature = (featureName) => {
  trackEvent('feature_use', {
    feature: featureName
  });
};

// Track session start
export const trackSessionStart = () => {
  sessionStartTime = Date.now();
  trackEvent('session_start', {});
};

// Track session end (call this on page unload)
export const trackSessionEnd = () => {
  const duration = Math.round((Date.now() - sessionStartTime) / 1000);
  
  // Use sendBeacon for reliable delivery on page unload
  const token = localStorage.getItem('token');
  if (token) {
    const data = JSON.stringify({
      event_type: 'session_end',
      device_type: getDeviceType(),
      duration: duration,
      timestamp: new Date().toISOString()
    });
    
    navigator.sendBeacon(
      `${API}/api/admin/track`,
      new Blob([data], { type: 'application/json' })
    );
  }
};

// Initialize tracking - call this once on app load
export const initAnalytics = () => {
  // Track session start
  trackSessionStart();
  
  // Track session end on page unload
  window.addEventListener('beforeunload', trackSessionEnd);
  
  // Track visibility changes (tab switching)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      // User is leaving/switching tabs
      if (currentPage && pageStartTime) {
        const duration = Math.round((Date.now() - pageStartTime) / 1000);
        trackEvent('page_view', {
          page: currentPage,
          duration: duration
        });
      }
    } else {
      // User is returning
      pageStartTime = Date.now();
    }
  });
};

// React hook for tracking page views
export const usePageTracking = (pageName) => {
  // This will be called when component mounts
  trackPageView(pageName);
};

export default {
  trackEvent,
  trackPageView,
  trackFeature,
  trackSessionStart,
  trackSessionEnd,
  initAnalytics,
  usePageTracking
};
